import { useEffect } from "react";
import { isRadioEnabled } from "./RadioToggle";
import { BoodaVoice } from "../lib/boodaVoice";

// bg-music + radio-ads pipeline.
//
// iOS native: routes through BoodaVoice → AVAudioEngine.musicPlayer →
// musicMixer → mainMixer → outputNode. SAME engine as voice + chimes
// + narration. The single-pipeline architecture means iOS's
// AVAudioSession governs all audio uniformly; no HTML5-vs-engine
// conflict, sample-accurate `scheduleFile` for the loop seam, and
// pauseMusic/resumeMusic let us honor app-backgrounded.
//
// Non-iOS (web preview / desktop / Android): routes through Web
// Audio API (AudioContext + AudioBufferSourceNode). The original
// implementation; works fine outside the iOS-VPIO-conflict context.
//
// Ads ride on Web Audio in BOTH cases for now. They're intermittent
// and skip when ctx is not "running" (so they don't fire phantom
// ducks while ctx is interrupted). Migrating ads onto the engine is
// a follow-on; for now they're orthogonal and any audible failure
// degrades gracefully (ad goes silent, music keeps playing).

const USE_NATIVE_MUSIC = BoodaVoice.supported;

// Radio-ad voiceovers. Each entry is either a string (no reward) or
// an object with `src` + optional `reward`. Reward = { code, kind,
// amount?, label }. When the ad plays through audibly + tab visible
// + once-per-day-per-code, BackgroundMusic dispatches a
// `radio:ad-reward` event that <RadioRewards/> consumes.
//
// `code` MUST be unique and stable per reward (e.g. "vu-2026-04").
// Re-recordings of the same ad keep the same code so the daily cap
// covers them as a single offer.
const RADIO_ADS = [
  { src: "/mp3/jukebox/radio-ads/promoting-upc-scanning.mp3" },
  { src: "/mp3/jukebox/radio-ads/promoting-using-chadwick.mp3" },
  { src: "/mp3/jukebox/radio-ads/mama-maggies-calendar.mp3" },
  { src: "/mp3/jukebox/radio-ads/mama-maggie-shopping-cart.mp3" },
  { src: "/mp3/jukebox/radio-ads/omalley-macros.mp3" },
  { src: "/mp3/jukebox/radio-ads/questionable-choices-ad.mp3" },
  { src: "/mp3/jukebox/radio-ads/chadwick-banner-ad.mp3" },
];

// ── Hourly background music ─────────────────────────────────────
// Each authored hour gets a {intro, loop, outro} triplet under
// /public/mp3/jukebox/bg-music/<hour-tag>/. The scheduler reads the
// current hour, plays the intro, loops the loop track until ANOTHER
// full loop iteration would cross the hour boundary, then plays the
// outro so the segment ends cleanly before the next hour's intro
// fires. Unauthored hours = silence; the scheduler re-checks every
// minute and kicks in when the user crosses into an authored hour.
//
// Hours are 0-23 in 24h time. To add a new hour: drop a folder with
// the three tracks in /public/mp3/jukebox/bg-music/, then add the
// entry here. No code changes needed beyond the registry.
const HOURLY_TRACKS = {
  // 8 AM (08:00) — picks up exactly when the 11 PM extended slot
  // outroes at 08:00, so the band hands off cleanly without a
  // fallback gap.
  8: {
    intro: "/mp3/jukebox/bg-music/8-am/8-am-intro.wav",
    loop:  "/mp3/jukebox/bg-music/8-am/8-am-loop.wav",
    outro: "/mp3/jukebox/bg-music/8-am/8-am-outro.wav",
  },
  // 10 AM (10:00)
  10: {
    intro: "/mp3/jukebox/bg-music/10-am/10-am-intro.mp3",
    loop:  "/mp3/jukebox/bg-music/10-am/10-am-loop.mp3",
    outro: "/mp3/jukebox/bg-music/10-am/10-am-outro.mp3",
  },
  // 4 PM (16:00)
  16: {
    intro: "/mp3/jukebox/bg-music/4-pm/4-pm-intro.mp3",
    loop:  "/mp3/jukebox/bg-music/4-pm/4-pm-loop.mp3",
    outro: "/mp3/jukebox/bg-music/4-pm/4-pm-outro.mp3",
  },
  // 6 PM (18:00)
  18: {
    intro: "/mp3/jukebox/bg-music/6-pm/6-pm-intro.wav",
    loop:  "/mp3/jukebox/bg-music/6-pm/6-pm-loop.wav",
    outro: "/mp3/jukebox/bg-music/6-pm/6-pm-outro.mp3",
  },
  // 9 PM (21:00)
  21: {
    intro: "/mp3/jukebox/bg-music/9-pm/9-pm-intro.wav",
    loop:  "/mp3/jukebox/bg-music/9-pm/9-pm-loop.wav",
    outro: "/mp3/jukebox/bg-music/9-pm/9-pm-outro.wav",
  },
  // 11 PM (23:00) — extended slot, runs 23:00 → 08:00 next day. The
  // outro fires once at 8 AM, after which fallback (peppermint lanes)
  // takes over until the 4 PM band.
  23: {
    intro: "/mp3/jukebox/bg-music/11-pm/11-pm-intro.mp3",
    loop:  "/mp3/jukebox/bg-music/11-pm/11-pm-loop.mp3",
    outro: "/mp3/jukebox/bg-music/11-pm/11-pm-outro.mp3",
  },
};

// "Peppermint Lanes" — kept as a SAFETY-NET fallback for when an
// authored band fails to load (missing file, decode error). In
// normal flow it's no longer reached: every wall-clock hour is now
// covered by an authored band that extends through the gap until
// the next authored hour. The 23 slot has always done this (23 →
// 08); now 8 → 10, 10 → 16, 16 → 18, 18 → 21, 21 → 23.
const FALLBACK_TRACK = {
  pt1: "/mp3/pinball-magic-loop-pt-1.mp3",
  pt2: "/mp3/pinball-magic-loop-pt-2.mp3",
};

// Sorted list of authored hours that ARE NOT the 23 carve-out.
// Used by currentBandHour to find the largest authored hour ≤ now,
// and by bandEndAt to find the next authored hour boundary.
const AUTHORED_HOURS_DAYTIME = Object.keys(HOURLY_TRACKS)
  .map(Number)
  .filter((h) => h !== 23)
  .sort((a, b) => a - b);

// Returns the hour key of the band that should be playing now. Each
// authored band extends until the next authored hour begins, so
// "gap" hours (9, 11–15, 17, 19–20, 22) inherit the previous
// authored hour's loop. 23 carve-out covers 23–08 separately.
function currentBandHour(now = new Date()) {
  const h = now.getHours();
  if (h >= 23 || h < 8) return 23;
  // h is in [8, 22]. Find the largest daytime authored hour ≤ h.
  let best = null;
  for (const a of AUTHORED_HOURS_DAYTIME) {
    if (a <= h) best = a;
    else break;
  }
  // best should always be non-null here because 8 ≤ h ≤ 22 and 8 is
  // authored; the loop guarantees we picked at least 8.
  return best;
}

// Wallclock Date at which `hour`'s outro should END. Each band runs
// until the NEXT authored hour starts:
//   23 → 08:00 (next day if h ≥ 23, today if h < 8)
//   21 → 23:00
//   18 → 21:00
//   16 → 18:00
//   10 → 16:00
//    8 → 10:00
function bandEndAt(hour, now = new Date()) {
  const d = new Date(now);
  if (hour === 23) {
    if (d.getHours() < 8) {
      d.setHours(8, 0, 0, 0);
    } else {
      d.setDate(d.getDate() + 1);
      d.setHours(8, 0, 0, 0);
    }
    return d;
  }
  // Daytime hour — find the next authored hour after this one. If
  // none (i.e. hour is 21, the last daytime band), end at 23:00 when
  // the 11 PM extended slot takes over.
  const next = AUTHORED_HOURS_DAYTIME.find((a) => a > hour);
  d.setHours(next != null ? next : 23, 0, 0, 0);
  return d;
}

// Natural duration of a band — what it would play if it started
// exactly at its hour. Used by the admin test panel so testing the
// 8 AM band at 9 PM still plays the full 2-hour envelope instead of
// skipping straight to the outro (which is what happens when
// bandEndAt's wall-clock math returns a past time).
function bandNaturalDurationSec(hour) {
  const start = new Date();
  start.setHours(hour, 0, 0, 0);
  const end = bandEndAt(hour, start);
  return Math.max(0, (end.getTime() - start.getTime()) / 1000);
}

// Music levels and ad cadence. Music plays at MUSIC_FULL_GAIN by
// default; an ad or Chadwick speech ducks it to MUSIC_DUCKED_GAIN.
const MUSIC_FULL_GAIN     = 0.6;
const MUSIC_DUCKED_GAIN   = 0.12;
const DUCK_RAMP_S         = 0.5;
const UNDUCK_RAMP_S       = 0.7;
// Wait before the FIRST ad so the user gets a moment of pure music
// at session start. Subsequent ads use the random interval window.
const FIRST_AD_DELAY_MS   = 45_000;       // 45s
const MIN_AD_INTERVAL_MS  = 2 * 60_000;   // 2 min between ads
const MAX_AD_INTERVAL_MS  = 5 * 60_000;   // up to 5 min between ads

export default function BackgroundMusic() {
  useEffect(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    // masterGain wraps the AD path (Web Audio). On both iOS and web,
    // ads ride this gain node so the radio mute toggle silences them.
    // Music has its OWN gain control: on iOS via the native
    // musicMixer (BoodaVoice.setMusicVolume), on web via musicGain
    // below.
    const masterGain = ctx.createGain();
    masterGain.gain.value = isRadioEnabled() ? 1 : 0;
    masterGain.connect(ctx.destination);

    // Music gain (Web Audio path only — iOS uses
    // BoodaVoice.setMusicVolume which controls the native musicMixer
    // directly).
    const musicGain = ctx.createGain();
    musicGain.gain.value = MUSIC_FULL_GAIN;
    musicGain.connect(masterGain);

    // Hour-aware band state. `bandHour` = the hour whose tracks are
    // currently scheduled (or null = silence). `bandSources` carries
    // the live Web Audio sources so cleanup + interrupt paths can
    // stop them. Unused on iOS (Swift handles its own active state).
    let bandHour = null;
    let bandSources = { intro: null, loop: null, outro: null };
    let fallbackSources = { pt1: null, pt2: null };
    let bandSwitchTimer = null;
    let visibilityCleanupRef = null;
    const bandBuffers = new Map();
    let started = false;
    let cancelled = false;
    let nextAdTimer = null;
    let currentAdSource = null;
    let cookModeActive = false;
    let chadwickSpeaking = false;
    const adBuffers = new Map();
    let lastAdIdx = -1;

    // ── Volume control (cross-path) ──────────────────────────────
    // iOS: BoodaVoice.setMusicVolume(target). The native musicMixer
    //      jumps to the target value — instant set, no in-JS ramp.
    //      For smooth fades we ramp via setTimeout (~30fps).
    // Web: musicGain.gain.linearRampToValueAtTime — sample-accurate
    //      Web Audio automation.
    const musicVolumeRampToken = { v: 0 };
    function rampMusicVolume(target, durationS) {
      if (USE_NATIVE_MUSIC) {
        musicVolumeRampToken.v += 1;
        const tok = musicVolumeRampToken.v;
        // Read current from a JS-side tracker since BoodaVoice
        // doesn't expose a getter.
        const start = currentMusicVolume;
        const startedAt = Date.now();
        const totalMs = Math.max(1, durationS * 1000);
        const tick = () => {
          if (cancelled || musicVolumeRampToken.v !== tok) return;
          const t = Math.min(1, (Date.now() - startedAt) / totalMs);
          currentMusicVolume = start + (target - start) * t;
          BoodaVoice.setMusicVolume(currentMusicVolume).catch(() => {});
          if (t < 1) setTimeout(tick, 33);
        };
        tick();
      } else {
        const t = ctx.currentTime;
        musicGain.gain.cancelScheduledValues(t);
        musicGain.gain.setValueAtTime(musicGain.gain.value, t);
        musicGain.gain.linearRampToValueAtTime(target, t + durationS);
      }
    }
    // Track the music volume independently of native state — used so
    // ramps know the start value (BoodaVoice has no getter).
    let currentMusicVolume = MUSIC_FULL_GAIN;

    async function loadBuffer(url) {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      return new Promise((resolve, reject) =>
        ctx.decodeAudioData(arr, resolve, reject),
      );
    }

    function pickAdEntry() {
      if (RADIO_ADS.length === 0) return null;
      if (RADIO_ADS.length === 1) return RADIO_ADS[0];
      let idx;
      do {
        idx = Math.floor(Math.random() * RADIO_ADS.length);
      } while (idx === lastAdIdx);
      lastAdIdx = idx;
      return RADIO_ADS[idx];
    }

    async function playAd() {
      if (cancelled || !started) return;
      // Suppression: CookMode owns the airwaves while mounted.
      // Reschedule for after CookMode closes — onCookmodeInactive
      // re-arms the timer.
      if (cookModeActive) return;
      // Skip silent ads. When ctx is "interrupted" / "suspended"
      // (iOS audio session has been claimed elsewhere), the ad's
      // AudioBufferSourceNode.start fires no actual sound but
      // onended fires after the buffer's duration — meaning the
      // ducker (rampMusicVolume to MUSIC_DUCKED_GAIN) would fire
      // for an inaudible ad and the user perceives "music ducking
      // with nobody talking." Skip + reschedule instead.
      if (ctx.state !== "running") {
        scheduleNextAd();
        return;
      }
      const entry = pickAdEntry();
      if (!entry) return;
      const url    = typeof entry === "string" ? entry : entry.src;
      const reward = typeof entry === "string" ? null  : entry.reward || null;
      if (!url) return;
      let buf = adBuffers.get(url);
      if (!buf) {
        try {
          buf = await loadBuffer(url);
          if (cancelled) return;
          adBuffers.set(url, buf);
        } catch {
          scheduleNextAd();
          return;
        }
      }

      // Ad runs through its own gain node at full volume so we don't
      // accidentally duck the ad alongside the music. Connects to
      // masterGain (not ctx.destination directly) so the radio mute
      // toggle silences ads too.
      const adGain = ctx.createGain();
      adGain.gain.value = 1.0;
      adGain.connect(masterGain);

      const adSource = ctx.createBufferSource();
      adSource.buffer = buf;
      adSource.connect(adGain);
      currentAdSource = adSource;

      // Duck music smoothly.
      rampMusicVolume(MUSIC_DUCKED_GAIN, DUCK_RAMP_S);

      // Start the ad slightly into the duck so the voice doesn't
      // step on the still-loud music tail.
      const t = ctx.currentTime;
      adSource.start(t + DUCK_RAMP_S * 0.6);

      adSource.onended = () => {
        currentAdSource = null;
        if (cancelled) return;
        // Unduck to full ONLY if the radio is still on — the user
        // may have toggled off mid-ad, and an unconditional ramp
        // here was one of the paths that resurrected muted music.
        rampMusicVolume(isRadioEnabled() ? MUSIC_FULL_GAIN : 0, UNDUCK_RAMP_S);

        // REWARD SEAL CHECK — only dispatch if the user actually
        // heard the ad. Three conditions:
        //   1. Audible: masterGain at end is > 0.01 (radio not muted).
        //   2. Foreground: document.visibilityState === "visible".
        //   3. Has reward: not every ad pays out.
        if (reward && reward.code) {
          const audible = masterGain.gain.value > 0.01;
          const visible = typeof document === "undefined"
            || document.visibilityState === "visible";
          if (audible && visible) {
            window.dispatchEvent(new CustomEvent("radio:ad-reward", {
              detail: { reward },
            }));
          }
        }

        scheduleNextAd();
      };
    }

    function scheduleNextAd() {
      if (cancelled) return;
      if (nextAdTimer) clearTimeout(nextAdTimer);
      const interval = MIN_AD_INTERVAL_MS
        + Math.random() * (MAX_AD_INTERVAL_MS - MIN_AD_INTERVAL_MS);
      nextAdTimer = setTimeout(playAd, interval);
    }

    // ── Web Audio band helpers (non-iOS only) ────────────────────
    async function loadBandBuffer(url) {
      if (!url) return null;
      if (bandBuffers.has(url)) return bandBuffers.get(url);
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const arr = await res.arrayBuffer();
        const buf = await new Promise((resolve, reject) =>
          ctx.decodeAudioData(arr, resolve, reject),
        );
        bandBuffers.set(url, buf);
        return buf;
      } catch (_) {
        return null;
      }
    }

    function stopBandSources() {
      for (const k of ["intro", "loop", "outro"]) {
        const s = bandSources[k];
        if (s && typeof s.stop === "function") {
          try { s.stop(); } catch (_) { /* ignore */ }
        }
        bandSources[k] = null;
      }
      // Native musicPlayer hosts both bands and fallback — one
      // stopMusic empties the whole pipeline. Idempotent in Swift.
      if (USE_NATIVE_MUSIC) BoodaVoice.stopMusic().catch(() => {});
    }

    function stopFallback() {
      for (const k of ["pt1", "pt2"]) {
        const s = fallbackSources[k];
        if (s && typeof s.stop === "function") {
          try { s.stop(); } catch (_) { /* ignore */ }
        }
        fallbackSources[k] = null;
      }
      if (USE_NATIVE_MUSIC) BoodaVoice.stopMusic().catch(() => {});
    }

    // ── Schedule fallback (peppermint lanes) ────────────────────
    async function scheduleFallback() {
      if (cancelled) return;
      if (fallbackSources.pt1 || fallbackSources.pt2) return;

      // iOS native path — hand the pair to BoodaVoice. The engine
      // plays pt1 once then re-schedules pt2 one-at-a-time. Sentinel
      // non-null values keep the idempotence guard above honest.
      if (USE_NATIVE_MUSIC) {
        fallbackSources.pt1 = "native";
        fallbackSources.pt2 = "native";
        try {
          await BoodaVoice.setMusicVolume(isRadioEnabled() ? MUSIC_FULL_GAIN : 0);
          currentMusicVolume = isRadioEnabled() ? MUSIC_FULL_GAIN : 0;
          await BoodaVoice.playMusicLoop({
            pt1Url: FALLBACK_TRACK.pt1,
            pt2Url: FALLBACK_TRACK.pt2,
          });
        } catch (e) {
          console.warn("[BackgroundMusic] native playMusicLoop failed:", e?.message || e);
          fallbackSources.pt1 = null;
          fallbackSources.pt2 = null;
        }
        return;
      }

      // Web Audio path (non-iOS).
      const [pt1, pt2] = await Promise.all([
        loadBandBuffer(FALLBACK_TRACK.pt1),
        loadBandBuffer(FALLBACK_TRACK.pt2),
      ]);
      if (cancelled) return;
      if (!pt1 || !pt2) return;
      if (bandHour !== null || currentBandHour() !== null) return;

      const t = ctx.currentTime;
      const pt1Src = ctx.createBufferSource();
      pt1Src.buffer = pt1;
      pt1Src.connect(musicGain);
      pt1Src.start(t);
      fallbackSources.pt1 = pt1Src;

      const pt2Src = ctx.createBufferSource();
      pt2Src.buffer = pt2;
      pt2Src.loop = true;
      pt2Src.connect(musicGain);
      pt2Src.start(t + Math.max(0, pt1.duration - 0.04));
      fallbackSources.pt2 = pt2Src;
    }

    // ── Schedule authored hour band ─────────────────────────────
    // `durationOverrideSec` (optional) — when set, use it as the
    // band length instead of `bandEndAt(hour) - now`. The admin
    // test-hour bridge passes the band's natural duration so
    // previewing the 8 AM band at 9 PM still plays the full 2-hour
    // envelope instead of immediately skipping to outro because the
    // wall-clock-derived boundary is in the past.
    async function scheduleBand(hour, durationOverrideSec) {
      if (cancelled) return;
      const tracks = HOURLY_TRACKS[hour];
      if (!tracks) {
        bandHour = null;
        return;
      }

      const durationSec = durationOverrideSec != null
        ? Math.max(0, durationOverrideSec)
        : Math.max(0, (bandEndAt(hour).getTime() - Date.now()) / 1000);

      // iOS native — Swift's playMusicEnvelope pre-validates URLs
      // and atomically swaps. If the new band's files are missing,
      // the prior schedule keeps playing instead of going silent.
      if (USE_NATIVE_MUSIC) {
        try {
          await BoodaVoice.setMusicVolume(isRadioEnabled() ? MUSIC_FULL_GAIN : 0);
          currentMusicVolume = isRadioEnabled() ? MUSIC_FULL_GAIN : 0;
          await BoodaVoice.playMusicEnvelope({
            introUrl: tracks.intro,
            loopUrl:  tracks.loop,
            outroUrl: tracks.outro,
            totalDurationSec: durationSec,
          });
          bandHour = hour;
          // Clear the fallback sentinels; envelope took over.
          fallbackSources.pt1 = null;
          fallbackSources.pt2 = null;
        } catch (e) {
          console.warn("[BackgroundMusic] native playMusicEnvelope failed:", e?.message || e);
          // Don't reset bandHour — prior schedule continues if any.
        }
        return;
      }

      // Web Audio path (non-iOS).
      const [intro, loop, outro] = await Promise.all([
        loadBandBuffer(tracks.intro),
        loadBandBuffer(tracks.loop),
        loadBandBuffer(tracks.outro),
      ]);
      if (cancelled) return;
      if (!intro || !loop || !outro) {
        bandHour = null;
        return;
      }

      stopBandSources();
      stopFallback();
      bandHour = hour;

      const audioStart  = ctx.currentTime;
      const audioEnd    = audioStart + durationSec;

      const introSrc = ctx.createBufferSource();
      introSrc.buffer = intro;
      introSrc.connect(musicGain);
      introSrc.start(audioStart);
      bandSources.intro = introSrc;
      const introEnd = audioStart + intro.duration;

      const outroDur = outro.duration;
      const outroStartTarget = audioEnd - outroDur;
      let outroStart = outroStartTarget;

      const roomForLoops = outroStartTarget - introEnd;
      const numLoops = Math.max(0, Math.floor(roomForLoops / loop.duration));
      const loopEnd  = introEnd + numLoops * loop.duration;

      if (numLoops > 0) {
        const loopSrc = ctx.createBufferSource();
        loopSrc.buffer = loop;
        loopSrc.loop = true;
        loopSrc.connect(musicGain);
        loopSrc.start(introEnd);
        loopSrc.stop(loopEnd);
        bandSources.loop = loopSrc;
        outroStart = loopEnd;
      } else if (introEnd < outroStartTarget) {
        outroStart = outroStartTarget;
      } else {
        outroStart = introEnd;
      }

      const outroSrc = ctx.createBufferSource();
      outroSrc.buffer = outro;
      outroSrc.connect(musicGain);
      outroSrc.start(outroStart);
      bandSources.outro = outroSrc;

      outroSrc.onended = () => {
        if (cancelled) return;
        bandSources.outro = null;
        const next = currentBandHour();
        if (next !== null && next !== bandHour) {
          scheduleBand(next);
        } else if (next === null) {
          bandHour = null;
          scheduleFallback().catch(() => {});
        }
      };
    }

    function tryStart() {
      if (started) return;
      // Radio OFF = engine OFF. Previously the engine always started
      // and the pref only zeroed the volume — but every unduck ramp,
      // foreground resumeMusic, and native envelope restore was a
      // path back to audible, so "off" didn't survive an app
      // relaunch. Don't start at all; the radio toggle's ON tap
      // calls tryStart itself (a user gesture, so the iOS audio
      // session unlock still works).
      if (!isRadioEnabled()) return;
      // Native music doesn't need ctx.state to be running — it lives
      // outside the Web Audio context. Web Audio music does need it.
      if (!USE_NATIVE_MUSIC && ctx.state !== "running") return;
      started = true;

      const h = currentBandHour();
      if (h !== null) {
        scheduleBand(h).catch(() => { /* graceful */ });
      } else {
        scheduleFallback().catch(() => {});
      }

      // Hour-change watcher + visibility listener.
      const evaluateSchedule = () => {
        if (cancelled) return;
        const h2 = currentBandHour();
        if (h2 !== null && h2 !== bandHour) {
          scheduleBand(h2).catch(() => {});
          return;
        }
        if (h2 === null) {
          if (!fallbackSources.pt1 && !fallbackSources.pt2) {
            stopBandSources();
            bandHour = null;
            scheduleFallback().catch(() => {});
          }
        }
      };
      bandSwitchTimer = setInterval(evaluateSchedule, 60_000);

      // Visibility listener handles BOTH the hour-change recovery
      // (came back from a long suspension) AND the user's app-
      // backgrounded preference (music should pause when minimized).
      const onVisibility = () => {
        if (typeof document === "undefined") return;
        if (document.visibilityState === "visible") {
          // Resume only when the radio is on — foreground resume was
          // another resurrection path for muted music.
          if (USE_NATIVE_MUSIC && isRadioEnabled()) BoodaVoice.resumeMusic().catch(() => {});
          evaluateSchedule();
        } else {
          // App backgrounded / tab hidden — pause music on iOS so the
          // user doesn't get bg-music in their pocket. The schedule
          // (and pendingMusicEnvelope snapshot) persist in Swift, so
          // resumeMusic on foreground picks back up where we left off.
          if (USE_NATIVE_MUSIC) BoodaVoice.pauseMusic().catch(() => {});
        }
      };
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibility);
      }
      visibilityCleanupRef = () => {
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibility);
        }
      };

      // First ad lands FIRST_AD_DELAY_MS after music starts. Only
      // schedule if the radio is currently unmuted; the toggle
      // handler will pick up the slot when the user unmutes.
      if (isRadioEnabled()) {
        nextAdTimer = setTimeout(playAd, FIRST_AD_DELAY_MS);
      }
    }

    const onInteraction = () => {
      if (started) return;
      if (ctx.state === "suspended") {
        ctx.resume().then(tryStart).catch(() => {});
      } else {
        tryStart();
      }
      // Native music path doesn't depend on ctx state — try to start
      // synchronously so the iOS gesture context can unlock the
      // native engine's audio session.
      if (USE_NATIVE_MUSIC) tryStart();
    };
    window.addEventListener("pointerdown", onInteraction);
    window.addEventListener("keydown", onInteraction);

    // Radio toggle — fade master in/out smoothly. Cancels the next
    // pending ad on mute; reschedules a fresh ad slot on unmute.
    const onRadioToggle = (e) => {
      const enabled = !!e?.detail?.enabled;
      // Turning ON when the engine never started (radio was off at
      // launch, so tryStart declined) — start it now. The toggle tap
      // is a user gesture, so the audio-session unlock applies.
      if (enabled && !started) {
        if (!USE_NATIVE_MUSIC && ctx.state === "suspended") {
          ctx.resume().then(tryStart).catch(() => {});
        } else {
          tryStart();
        }
      }
      // Turning OFF: pause the native engine outright — volume-0
      // playback kept the audio session alive and one resume away
      // from audible.
      if (!enabled && USE_NATIVE_MUSIC && started) {
        BoodaVoice.pauseMusic().catch(() => {});
      }
      if (enabled && USE_NATIVE_MUSIC && started) {
        BoodaVoice.resumeMusic().catch(() => {});
      }
      const t = ctx.currentTime;
      // Ads (Web Audio) — fade masterGain over 0.4s.
      masterGain.gain.cancelScheduledValues(t);
      masterGain.gain.setValueAtTime(masterGain.gain.value, t);
      masterGain.gain.linearRampToValueAtTime(enabled ? 1 : 0, t + 0.4);
      // Music — fade the music gain (native musicMixer on iOS, Web
      // Audio musicGain on web).
      rampMusicVolume(enabled ? MUSIC_FULL_GAIN : 0, 0.4);
      if (enabled) {
        if (started && !nextAdTimer && !currentAdSource && !cookModeActive) {
          scheduleNextAd();
        }
      } else {
        if (nextAdTimer) { clearTimeout(nextAdTimer); nextAdTimer = null; }
        // Don't stop currentAdSource — let it finish (silently);
        // onended already reschedules. Stopping mid-ad would
        // produce a crackle on a re-toggle.
      }
    };
    window.addEventListener("radio:toggle", onRadioToggle);

    // CookMode lifecycle — ad suppression only. Music keeps playing
    // because narration + music coexist on the same engine pipeline.
    const onCookmodeActive = () => {
      cookModeActive = true;
      if (nextAdTimer) { clearTimeout(nextAdTimer); nextAdTimer = null; }
      if (currentAdSource) {
        try { currentAdSource.stop(); } catch (_) { /* ignore */ }
      }
    };
    const onCookmodeInactive = () => {
      cookModeActive = false;
      if (started && !nextAdTimer && !currentAdSource && isRadioEnabled()) {
        scheduleNextAd();
      }
    };
    window.addEventListener("cookmode:active", onCookmodeActive);
    window.addEventListener("cookmode:inactive", onCookmodeInactive);

    // Chadwick voice-line ducker. Same target as the ad-duck so they
    // never fight each other.
    const onChadwickStart = () => {
      chadwickSpeaking = true;
      rampMusicVolume(MUSIC_DUCKED_GAIN, DUCK_RAMP_S);
    };
    const onChadwickEnd = () => {
      chadwickSpeaking = false;
      if (currentAdSource) return;
      rampMusicVolume(MUSIC_FULL_GAIN, UNDUCK_RAMP_S);
    };
    window.addEventListener("chadwick:speak-start", onChadwickStart);
    window.addEventListener("chadwick:speak-end", onChadwickEnd);

    // Admin test-hour bridge. detail = { hour: <number> }
    // force-schedules that authored hour using its NATURAL duration
    // (what it would play if started at its wall-clock hour). Without
    // the override, bandEndAt's wall-clock math would compute a past
    // boundary for any band tested outside its slot and the envelope
    // would skip straight to the outro.
    //
    // detail = { hour: null } | "live" clears the override and
    // snaps back to the wall-clock schedule.
    const onTestHour = (e) => {
      const det = e && e.detail;
      if (!det) return;
      if (det.hour == null || det.hour === "live") {
        stopBandSources();
        stopFallback();
        bandHour = null;
        const h = currentBandHour();
        if (h !== null) scheduleBand(h).catch(() => {});
        else scheduleFallback().catch(() => {});
        return;
      }
      const target = Number(det.hour);
      if (!Number.isFinite(target) || !HOURLY_TRACKS[target]) return;
      bandHour = null;
      scheduleBand(target, bandNaturalDurationSec(target)).catch(() => {});
    };
    window.addEventListener("bgMusic:testHour", onTestHour);

    // Native musicEnvelopeEnded → mirrors outroSrc.onended in the
    // Web Audio path. Fires from Swift when the engine's envelope
    // outro finishes; we then advance to whatever the current hour
    // says (authored band → scheduleBand, unauthored → fallback).
    let musicEndedListenerHandle = null;
    if (USE_NATIVE_MUSIC) {
      BoodaVoice.addListener("musicEnvelopeEnded", () => {
        if (cancelled) return;
        const next = currentBandHour();
        if (next !== null && next !== bandHour) {
          scheduleBand(next).catch(() => {});
        } else if (next === null) {
          bandHour = null;
          scheduleFallback().catch(() => {});
        }
      }).then((h) => { musicEndedListenerHandle = h; })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("radio:toggle", onRadioToggle);
      window.removeEventListener("cookmode:active", onCookmodeActive);
      window.removeEventListener("cookmode:inactive", onCookmodeInactive);
      window.removeEventListener("chadwick:speak-start", onChadwickStart);
      window.removeEventListener("chadwick:speak-end", onChadwickEnd);
      window.removeEventListener("bgMusic:testHour", onTestHour);
      if (musicEndedListenerHandle && typeof musicEndedListenerHandle.remove === "function") {
        try { musicEndedListenerHandle.remove(); } catch { /* ignore */ }
      }
      if (USE_NATIVE_MUSIC) BoodaVoice.stopMusic().catch(() => {});
      if (nextAdTimer) { clearTimeout(nextAdTimer); nextAdTimer = null; }
      if (bandSwitchTimer) { clearInterval(bandSwitchTimer); bandSwitchTimer = null; }
      if (visibilityCleanupRef) { visibilityCleanupRef(); visibilityCleanupRef = null; }
      stopBandSources();
      stopFallback();
      try { currentAdSource && currentAdSource.stop(); } catch { /* ignore */ }
      ctx.close().catch(() => {});
    };
  }, []);

  return null;
}
